import type { z } from 'zod';
import {
    ageBracketBreakdownRowSchema,
    brandBreakdownRowSchema,
    businessUnitBreakdownRowSchema,
    dateRangeSchema,
    degradationCauseSchema,
    deviceBreakdownRowSchema,
    envelopeBreakdownRowSchema,
    envelopeBreakdownSchema,
    envelopeCapabilitiesSchema,
    envelopeCaveatSchema,
    envelopeDegradationSchema,
    envelopeMetricSchema,
    envelopeSeriesPointSchema,
    granularitySchema,
    kpiEnvelopeSchema,
    provinceBreakdownRowSchema,
    ratioCompareSchema,
    ratioMetricSchema,
    regionGrainSchema,
    scalarCompareSchema,
    scalarMetricSchema,
    visitMilestoneBreakdownRowSchema,
} from './kpi-envelope.schema';

/**
 * Frontend view of the backend standardized KPI envelope. Every type is inferred
 * from the vendored zod schema (`kpi-envelope.schema.ts`), so the frontend has a
 * single definition that is the runtime validator and the types at once: the CMS
 * types and its boundary validation cannot drift from each other. (They can still
 * drift from the backend, which the vendored copy tracks by hand; see the sync note
 * in `kpi-envelope.schema.ts`.) Metrics are keyed by id; each is `scalar` (a
 * single value) or `ratio`. `series` carries a per-day breakdown when
 * `granularity=daily`; `breakdowns` carries zero or more dimensional splits,
 * including the ordered funnel a `columns` card renders.
 * `degradation` is present when the envelope has no data to serve; a value
 * that merely needs qualifying travels as a `warning` instead.
 * `dataCompleteThrough` is present only for a KPI whose source lags the requested
 * period, and says how far this envelope's own data is complete.
 * `compareRange` and per-metric `compare` carry compare-period data; the
 * frontend computes the delta.
 */
export type MetricType = 'scalar' | 'ratio';

/** Compare-period value for a `scalar` metric; the frontend computes the delta. */
export type ScalarCompare = z.infer<typeof scalarCompareSchema>;

/** Compare-period values for a `ratio` metric; the frontend computes the delta. */
export type RatioCompare = z.infer<typeof ratioCompareSchema>;

/** A single-value metric (e.g. `newUsers`). */
export type ScalarMetric = z.infer<typeof scalarMetricSchema>;

/** A ratio metric (e.g. `ageGatedRate`); `rate` is null when the denominator is 0. */
export type RatioMetric = z.infer<typeof ratioMetricSchema>;

/**
 * Discriminated on `type`: the compiler ties `value` to `scalar` and
 * `numerator/denominator/rate` to `ratio` (for the metric and its `compare`
 * block). Consumers narrow on `type` instead of reading loose optional fields.
 */
export type EnvelopeMetric = z.infer<typeof envelopeMetricSchema>;

export type EnvelopeCapabilities = z.infer<typeof envelopeCapabilitiesSchema>;

/** Region grain the endpoint actually resolved (see `envelopeCapabilitiesSchema`
 * in `kpi-envelope.schema.ts` for why this supersedes the deprecated `provinces`
 * boolean). */
export type RegionGrain = z.infer<typeof regionGrainSchema>;

/** A per-day point. `date` is ISO `YYYY-MM-DD`; the rest are metric values keyed by metric id. */
export type EnvelopeSeriesPoint = z.infer<typeof envelopeSeriesPointSchema>;

/** A row on an open-key axis (`round`, `badge`, `bonusAction`), whose `key` is a
 * runtime id with no finite domain. Closed-key axes have their own row types. */
export type EnvelopeBreakdownRow = z.infer<typeof envelopeBreakdownRowSchema>;

export type ProvinceBreakdownRow = z.infer<typeof provinceBreakdownRowSchema>;

export type BusinessUnitBreakdownRow = z.infer<
    typeof businessUnitBreakdownRowSchema
>;

export type BrandBreakdownRow = z.infer<typeof brandBreakdownRowSchema>;

export type DeviceBreakdownRow = z.infer<typeof deviceBreakdownRowSchema>;

export type AgeBracketBreakdownRow = z.infer<
    typeof ageBracketBreakdownRowSchema
>;

export type VisitMilestoneBreakdownRow = z.infer<
    typeof visitMilestoneBreakdownRowSchema
>;

/** Discriminated on `by`, so the row key type is tied to the axis: the compiler
 * rejects a business-unit key under `by: 'province'`. */
export type EnvelopeBreakdown = z.infer<typeof envelopeBreakdownSchema>;

/** The axis name of a breakdown. */
export type BreakdownAxis = EnvelopeBreakdown['by'];

export type EnvelopeDegradation = z.infer<typeof envelopeDegradationSchema>;

/** One human-facing note on the envelope: a permanent `caveat`, or a `warning`
 * that says something bit THIS response and carries a stable `code`. */
export type EnvelopeCaveat = z.infer<typeof envelopeCaveatSchema>;

export type DegradationCause = z.infer<typeof degradationCauseSchema>;

/** The warnings only. Empty when nothing is off with this response, which is
 * the common case; the info bubble colours on this being non-empty. */
export function envelopeWarnings(
    caveats: readonly EnvelopeCaveat[]
): Extract<EnvelopeCaveat, { kind: 'warning' }>[] {
    return caveats.filter(
        (entry): entry is Extract<EnvelopeCaveat, { kind: 'warning' }> =>
            entry.kind === 'warning'
    );
}

export type DateRange = z.infer<typeof dateRangeSchema>;

/** Time aggregation for a KPI request: period totals or a per-day series. */
export type Granularity = z.infer<typeof granularitySchema>;

export type KpiEnvelope = z.infer<typeof kpiEnvelopeSchema>;

/** The canonical schema, re-exported so consumers validate against the same
 * definition the types come from (see `AnalyticsStore.fetchKpiEnvelope`). */
export { kpiEnvelopeSchema };
